/**
 * Sign-in. Production uses Google Cloud Identity Platform (email + password,
 * TOTP second factor, optional SSO); local development uses a dev token.
 * The ID token lives in memory and in sessionStorage (cleared when the tab
 * closes), never in localStorage.
 */
import type { FirebaseApp } from "firebase/app";
import type { Auth, MultiFactorResolver, User } from "firebase/auth";

export interface PublicConfig {
  authMode: "dev" | "identity-platform";
  requireMfa: boolean;
  identityPlatform: { apiKey: string; authDomain: string; projectId: string } | null;
  demoProviders: boolean;
  languages: Array<{ code: string; name: string; nativeName: string; support: string; sttStatus: string; ttsNote: string | null }>;
  focusModules: Array<{ key: string; name: string; summary: string; country: string | null }>;
  plans: Record<string, { interviewSeconds: number | null; label: string }>;
}

let config: PublicConfig | null = null;
let fbAuth: Auth | null = null;
let devToken: string | null = sessionStorageGet("gk.devToken");
const listeners = new Set<() => void>();

function sessionStorageGet(k: string): string | null {
  try {
    return sessionStorage.getItem(k);
  } catch {
    return null;
  }
}
function sessionStorageSet(k: string, v: string | null) {
  try {
    if (v == null) sessionStorage.removeItem(k);
    else sessionStorage.setItem(k, v);
  } catch {
    /* private mode */
  }
}

export async function loadConfig(): Promise<PublicConfig> {
  if (config) return config;
  const res = await fetch("/api/config");
  config = (await res.json()) as PublicConfig;
  if (config.authMode === "identity-platform" && config.identityPlatform) {
    const { initializeApp } = await import("firebase/app");
    const { getAuth, onIdTokenChanged } = await import("firebase/auth");
    const app: FirebaseApp = initializeApp(config.identityPlatform);
    fbAuth = getAuth(app);
    onIdTokenChanged(fbAuth, () => listeners.forEach((l) => l()));
    await fbAuth.authStateReady();
  }
  return config;
}

export function onAuthChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function getToken(): Promise<string | null> {
  if (config?.authMode === "identity-platform") return (await fbAuth?.currentUser?.getIdToken()) ?? null;
  return devToken;
}

export function isSignedIn(): boolean {
  return config?.authMode === "identity-platform" ? Boolean(fbAuth?.currentUser) : Boolean(devToken);
}

export async function devSignIn(email: string, name: string): Promise<void> {
  const res = await fetch("/api/auth/dev-login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, name }),
  });
  if (!res.ok) throw new Error("Sign-in failed");
  devToken = ((await res.json()) as { token: string }).token;
  sessionStorageSet("gk.devToken", devToken);
  listeners.forEach((l) => l());
}

export type SignInResult =
  | { kind: "done" }
  | { kind: "mfa"; resolver: MultiFactorResolver }
  | { kind: "enroll"; user: User }
  | { kind: "verify"; email: string };

/** Emails a link to set (first time) or reset a password. Silent if the address has no account. */
export async function sendPasswordSetup(email: string): Promise<void> {
  const { sendPasswordResetEmail } = await import("firebase/auth");
  try {
    await sendPasswordResetEmail(fbAuth!, email);
  } catch (err) {
    const code = (err as { code?: string }).code;
    if (code === "auth/user-not-found") return; // don't reveal which addresses have accounts
    throw new Error(friendly(code));
  }
}

export async function passwordSignIn(email: string, password: string): Promise<SignInResult> {
  const { signInWithEmailAndPassword, getMultiFactorResolver, multiFactor } = await import("firebase/auth");
  try {
    const cred = await signInWithEmailAndPassword(fbAuth!, email, password);
    if (!cred.user.emailVerified) {
      // The platform accepts only verified addresses; send the link and sign out until it is opened.
      const { sendEmailVerification, signOut: fbSignOut } = await import("firebase/auth");
      await sendEmailVerification(cred.user).catch(() => undefined);
      await fbSignOut(fbAuth!);
      return { kind: "verify", email };
    }
    if (config?.requireMfa && multiFactor(cred.user).enrolledFactors.length === 0) return { kind: "enroll", user: cred.user };
    return { kind: "done" };
  } catch (err) {
    if ((err as { code?: string }).code === "auth/multi-factor-auth-required") {
      return { kind: "mfa", resolver: getMultiFactorResolver(fbAuth!, err as never) };
    }
    throw new Error(friendly((err as { code?: string }).code));
  }
}

export async function completeTotp(resolver: MultiFactorResolver, code: string): Promise<void> {
  const { TotpMultiFactorGenerator } = await import("firebase/auth");
  const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) ?? resolver.hints[0]!;
  await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code));
}

/** First sign-in when two-step verification is required: show the secret as a QR, confirm a code. */
export async function startTotpEnrollment(user: User): Promise<{ uri: string; finish: (code: string) => Promise<void> }> {
  const { multiFactor, TotpMultiFactorGenerator } = await import("firebase/auth");
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return {
    uri: secret.generateQrCodeUrl(user.email ?? "user", "Kriti Voice by Gramin Kriti"),
    finish: async (code: string) => {
      await multiFactor(user).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code), "Authenticator app");
      await user.getIdToken(true);
    },
  };
}

export async function signOut(): Promise<void> {
  if (config?.authMode === "identity-platform") {
    const { signOut: fbSignOut } = await import("firebase/auth");
    await fbSignOut(fbAuth!);
  }
  devToken = null;
  sessionStorageSet("gk.devToken", null);
  listeners.forEach((l) => l());
}

function friendly(code?: string): string {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "That email and password don't match. Check them and try again.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes, then try again.";
    default:
      return "Sign-in didn't work. Check your connection and try again.";
  }
}
